import CloseIcon from '@mui/icons-material/Close'
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined'
import Box from '@mui/material/Box'
import { FC, ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import IconButton from '../components/IconButton'

interface SidePanelProps {
    open: boolean
    onClose: () => void
    children: ReactNode
    /**
     * Custom width for the side panel.
     * Defaults to '828px'.
     */
    width?: string
    /**
     * If defined, renders the ReactNode on the right side
     * of the panel's header.
     */
    headerContent?: ReactNode
    /**
     * If true, keeps the panel header sticky to the top
     * when content is scrolling.
     * Defaults to false.
     */
    stickyHeader?: boolean
    /**
     * If defined, shows an expand button on the header
     * and executes the callback on button click.
     */
    onExpandClick?: () => void
}

const PANEL_DEFAULT_WIDTH = '828px'

const SidePanel: FC<SidePanelProps> = ({
    open,
    onClose,
    children,
    stickyHeader,
    onExpandClick,
    headerContent,
    width,
}) => {
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)

    useEffect(() => {
        if (open) {
            // Create portal container element
            const container = document.createElement('div')
            container.setAttribute('id', 'side-panel-portal')
            document.body.appendChild(container)
            setPortalContainer(container)

            return () => {
                // Cleanup: remove the portal container
                document.body.removeChild(container)
                setPortalContainer(null)
            }
        }
    }, [open])

    useEffect(() => {
        if (!open) {
            return
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [open, onClose])

    if (!open || !portalContainer) {
        return null
    }

    return createPortal(
        <Box
            onClick={() => onClose()}
            sx={{
                boxSizing: 'border-box',
                '*': {
                    boxSizing: 'border-box',
                },
                width: '100vw',
                height: '100vh',
                position: 'fixed',
                left: 0,
                top: 0,
                backgroundColor: `BlackTransparent20`,
                zIndex: 99,
                animation: 'show-overlay .1s ease-in-out forwards',
                '@keyframes show-overlay': {
                    '0%': {
                        opacity: 0,
                    },
                    '100%': {
                        opacity: 1,
                    },
                },
            }}
        >
            <Box
                onClick={(e) => {
                    // don't close overlay on this click
                    e.stopPropagation()
                }}
                sx={{
                    width: {
                        xs: '80%',
                        md: width ?? PANEL_DEFAULT_WIDTH,
                    },
                    overflow: 'auto',
                    height: '100vh',
                    position: 'absolute',
                    top: 0,
                    zIndex: 9,
                    display: 'flex',
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    animation: 'show-sidebar .3s ease-in-out forwards',
                    boxShadow: 8,
                    backgroundColor: `Surface100`,
                    boxSizing: 'border-box',
                    '@keyframes show-sidebar': {
                        '0%': {
                            right: '-200px',
                            opacity: 0,
                        },
                        '30%': {
                            opacity: 1,
                        },
                        '100%': {
                            right: '0',
                            opacity: 1,
                        },
                    },
                }}
            >
                {/* Sticky header */}
                <Box
                    sx={{
                        backgroundColor: `Surface100`,
                        position: stickyHeader ? 'sticky' : 'initial',
                        top: 0,
                        width: '100%',
                        px: 4,
                        py: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        ...(stickyHeader && {
                            zIndex: 1,
                        }),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <IconButton
                            style={'floating'}
                            size={'sm'}
                            onClick={() => {
                                onClose()
                            }}
                            leadingIcon={<CloseIcon />}
                        />
                        {!!onExpandClick && (
                            <IconButton
                                style={'floating'}
                                size={'sm'}
                                onClick={() => {
                                    onExpandClick()
                                }}
                                leadingIcon={<OpenInFullOutlinedIcon />}
                            />
                        )}
                    </Box>
                    {headerContent}
                </Box>
                {/* Content */}
                <Box
                    sx={{
                        px: 4,
                        py: 3,
                        width: '100%',
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>,
        portalContainer,
    )
}

export default SidePanel
